import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { contributionAPI, adminAPI, repositoryAPI, userAPI } from '../services/api'
import { enrichContributionsWithRepositorySlugs } from '../utils/enrichContributionsWithRepositorySlugs'
import ContributionCard from '../components/ContributionCard'
import './Page.css'
import './Dashboard.css'

const SUBMISSIONS_PAGE_SIZE = 100

function DashboardSubmissions() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [contributions, setContributions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [repositorySlugs, setRepositorySlugs] = useState({})
  const [selectedContribution, setSelectedContribution] = useState(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [showApprovedMessage, setShowApprovedMessage] = useState({})
  const [myRepos, setMyRepos] = useState([])

  useEffect(() => {
    loadContributions()
    loadMyRepositories()
  }, [])

  const loadContributions = async () => {
    try {
      setIsLoading(true)
      const response = await contributionAPI.list({ page_size: SUBMISSIONS_PAGE_SIZE })
      let list = response.contributions || []
      try {
        const { contributions: enriched, slugByRepoId } = await enrichContributionsWithRepositorySlugs(
          list,
          (params) => repositoryAPI.list(params)
        )
        list = enriched
        setRepositorySlugs((prev) => {
          const next = { ...prev, ...slugByRepoId }
          for (const c of list) {
            if (c.repository_id && c.repository_slug) {
              next[c.repository_id] = c.repository_slug
            }
          }
          return next
        })
      } catch (slugErr) {
        console.error('Failed to resolve repository slugs:', slugErr)
      }
      setContributions(list)
    } catch (error) {
      console.error('Failed to load contributions:', error)
      setContributions([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadMyRepositories = async () => {
    try {
      const response = await userAPI.getMyRepositories(1, 100)
      setMyRepos(response.repositories || [])
    } catch (error) {
      console.error('Failed to load my repositories:', error)
      setMyRepos([])
    }
  }

  const getRepositorySlug = (contrib) => {
    if (contrib.repository_slug) return contrib.repository_slug
    if (contrib.repository_id && repositorySlugs[contrib.repository_id]) return repositorySlugs[contrib.repository_id]
    return null
  }

  const getSubmissionTypeLabel = (type) => {
    const labels = {
      new_repository: 'New repository',
      new_commit: 'New commit',
      new_issue: 'New issue',
      circuit_notes: 'Circuit notes',
    }
    return labels[type] || type
  }

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'contribution-status-draft',
      pending: 'contribution-status-pending',
      approved: 'contribution-status-approved',
      rejected: 'contribution-status-rejected',
    }
    return classes[status] || 'contribution-status-default'
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleEditContribution = (contributionId) => {
    navigate('/dashboard', { state: { editContributionId: contributionId } })
  }

  const handleApprove = async (contributionId) => {
    if (!window.confirm('Are you sure you want to approve this contribution?')) return
    try {
      setIsProcessing(true)
      if (user?.role === 'admin') {
        try {
          await adminAPI.approveContribution(contributionId)
        } catch {
          await contributionAPI.approve(contributionId)
        }
      } else {
        await contributionAPI.approve(contributionId)
      }
      await loadContributions()
      setShowApprovedMessage((prev) => ({ ...prev, [contributionId]: true }))
      setTimeout(() => {
        setShowApprovedMessage((prev) => {
          const next = { ...prev }
          delete next[contributionId]
          return next
        })
      }, 5000)
    } catch (error) {
      console.error('Approval error:', error)
      alert(error.message || 'Failed to approve contribution')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRejectClick = (contribution) => {
    setSelectedContribution(contribution)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason')
      return
    }
    try {
      setIsProcessing(true)
      await contributionAPI.reject(selectedContribution.id, rejectReason)
      setShowRejectModal(false)
      setSelectedContribution(null)
      setRejectReason('')
      await loadContributions()
    } catch (error) {
      alert(error.message || 'Failed to reject contribution')
    } finally {
      setIsProcessing(false)
    }
  }

  const forkRepos = myRepos.filter((r) => r.parent_repository_id)
  const managedRepositoryIds = new Set(myRepos.map((repo) => repo.id))
  const activityItems = [
    ...contributions.map((c) => ({ type: 'contribution', date: c.submitted_at || c.created_at, contrib: c })),
    ...forkRepos.map((r) => ({ type: 'fork', date: r.created_at, repo: r })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date))

  return (
    <div className="page-container">
      <div className="dashboard-header" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/dashboard" className="btn btn-secondary" style={{ flexShrink: 0 }}>
            ← Dashboard
          </Link>
          <h1 style={{ margin: 0 }}>Your Activity</h1>
        </div>
        <p className="dashboard-subtitle">All your contributions, forks, and activity</p>
      </div>

      <div className="dashboard-card">
        {isLoading && contributions.length === 0 ? (
          <div className="empty-state">
            <p>Loading...</p>
          </div>
        ) : activityItems.length === 0 ? (
          <div className="empty-state">
            <p>No activity yet.</p>
            <Link to="/dashboard" className="btn btn-primary" style={{ marginTop: '1rem' }}>
              Go to Dashboard
            </Link>
          </div>
        ) : (
          <div className="contributions-list">
            {activityItems.map((item) =>
              item.type === 'fork' ? (
                <div
                  key={`fork-${item.repo.id}`}
                  className="contribution-item"
                  style={{ cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '0.5rem' }}
                  onClick={() => navigate(`/repositories/${item.repo.slug}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/repositories/${item.repo.slug}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="contribution-item-header">
                    <span className="contribution-item-title">
                      Forked{' '}
                      <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{item.repo.title}</span>
                      {item.repo.parent_repository_slug && (
                        <>
                          {' '}from{' '}
                          <Link to={`/repositories/${item.repo.parent_repository_slug}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600, color: 'var(--accent)' }}>
                            {item.repo.parent_repository_title || item.repo.parent_repository_slug}
                          </Link>
                        </>
                      )}
                    </span>
                    <span className="contribution-status-badge" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Fork</span>
                  </div>
                  <div className="contribution-item-meta">
                    <span>{formatDate(item.date)}</span>
                  </div>
                </div>
              ) : (
                <ContributionCard
                  key={item.contrib.id}
                  contrib={item.contrib}
                  repoSlug={getRepositorySlug(item.contrib)}
                  user={user}
                  showApprovedMessage={showApprovedMessage}
                  formatDate={formatDate}
                  getSubmissionTypeLabel={getSubmissionTypeLabel}
                  getStatusBadgeClass={getStatusBadgeClass}
                  onEdit={handleEditContribution}
                  onApprove={handleApprove}
                  onReject={handleRejectClick}
                  onCardClick={(slug) => navigate(`/repositories/${slug}`)}
                  isProcessing={isProcessing}
                  managedRepositoryIds={managedRepositoryIds}
                />
              )
            )}
          </div>
        )}
      </div>

      {showRejectModal && selectedContribution && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary)',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 style={{ marginBottom: '1rem' }}>Reject Contribution</h2>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
              Please provide a reason for rejecting this contribution. The contributor will see this reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                marginBottom: '1rem',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(false)
                  setSelectedContribution(null)
                  setRejectReason('')
                }}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleRejectSubmit}
                disabled={isProcessing || !rejectReason.trim()}
                style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }}
              >
                {isProcessing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardSubmissions
